import React, { useEffect, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import { Card } from '../ui';
import leaveApi from './leaveApi';

const ApplyLeave = () => {
  // Log screen view
  useScreenLogging('ApplyLeave', {
    component: 'ApplyLeave',
  });
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedLeaveType, setSelectedLeaveType] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [existingLeaveRequests, setExistingLeaveRequests] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  // Leave balance functionality removed - not available in backend yet
  // const [leaveBalance, setLeaveBalance] = useState(null);
  // const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  const leaveTypes = leaveApi.getLeaveTypes();

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    fetchExistingLeaveRequests();
    // fetchLeaveBalance(); // Commented out - leave balance not available in backend
  }, []);

  const navigateMonth = direction => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };

  const fetchExistingLeaveRequests = async () => {
    setIsLoadingRequests(true);
    try {
      const response = await leaveApi.getMyLeaveRequests();
      setExistingLeaveRequests(response.leave_requests || []);
      
      // Log successful API call
      loggingService.logInfo(
        'leaveFetchRequests',
        'Successfully fetched existing leave requests',
        {
          endpoint: '/leave',
          method: 'GET',
          requestCount: response.leave_requests?.length || 0,
        }
      ).catch(logError => {
        console.warn('[ApplyLeave] Failed to log event:', logError);
      });
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      // Error logging is handled in leaveApi.js
    } finally {
      setIsLoadingRequests(false);
    }
  };

  // Leave balance functionality removed - not available in backend yet
  // const fetchLeaveBalance = async () => {
  //   setIsLoadingBalance(true);
  //   try {
  //     const response = await leaveApi.getLeaveBalance();
  //     setLeaveBalance(response);
  //   } catch (error) {
  //     console.error('Error fetching leave balance:', error);
  //     // Set default balance if API fails
  //     setLeaveBalance({
  //       casual_leave: 12,
  //       sick_leave: 7,
  //       earned_leave: 21,
  //       compensatory_off: 0,
  //       loss_of_pay: 0,
  //     });
  //   } finally {
  //     setIsLoadingBalance(false);
  //   }
  // };

  const isDateSelectable = date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Don't allow past dates
    if (date < today) return false;

    // Check if date has existing leave request
    const dateString = leaveApi.formatDateForApi(date);
    return !existingLeaveRequests.some(
      request =>
        request.date_list &&
        request.date_list.includes(dateString) &&
        (request.status === 'pending' || request.status === 'approved')
    );
  };

  const handleDateSelect = date => {
    if (!isDateSelectable(date)) return;

    setSelectedDates(prev => {
      const isSelected = prev.some(d => d.getTime() === date.getTime());
      if (isSelected) {
        return prev.filter(d => d.getTime() !== date.getTime());
      } else {
        return [...prev, date].sort((a, b) => a - b);
      }
    });
  };

  const removeDate = dateToRemove => {
    setSelectedDates(prev =>
      prev.filter(date => date.getTime() !== dateToRemove.getTime())
    );
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (selectedDates.length === 0) {
      setError('Please select at least one date');
      return;
    }

    if (!selectedLeaveType) {
      setError('Please select a leave type');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for leave');
      return;
    }

    setIsSubmitting(true);

    try {
      const leaveData = {
        dateList: selectedDates.map(date => leaveApi.formatDateForApi(date)),
        leaveType: selectedLeaveType,
        numberOfDays: selectedDates.length,
        reason: reason.trim(),
      };

      const response = await leaveApi.createLeaveRequest(leaveData);

      // Log successful API call
      loggingService.logInfo(
        'leaveCreateRequest',
        'Successfully created leave request',
        {
          endpoint: '/leave',
          method: 'POST',
          leaveType: selectedLeaveType,
          numberOfDays: selectedDates.length,
          dateList: leaveData.dateList,
          requestId: response.request_id || response.leave_request_id || null,
        }
      ).catch(logError => {
        console.warn('[ApplyLeave] Failed to log event:', logError);
      });

      setSuccess(true);
      setSelectedDates([]);
      setSelectedLeaveType('');
      setReason('');

      // Refresh existing requests
      await fetchExistingLeaveRequests();

      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error creating leave request:', error);
      setError(error.message || 'Failed to submit leave request');
      // Error logging is handled in leaveApi.js
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCalendar = () => {
    const today = new Date();
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const totalCells = 42; // 6 weeks * 7 days
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      const prevMonthDate = new Date(
        currentYear,
        currentMonth,
        i - firstDay + 1
      );
      days.push(
        <div key={`prev-${i}`} className="calendar-day other-month">
          <span className="day-number">{prevMonthDate.getDate()}</span>
        </div>
      );
    }

    // Add days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = selectedDates.some(
        d => d.toDateString() === date.toDateString()
      );
      const isSelectable = isDateSelectable(date);
      const hasExistingRequest =
        !isSelectable &&
        isDateSelectable(new Date(date.getTime() - 24 * 60 * 60 * 1000));

      days.push(
        <div
          key={day}
          className={`calendar-day current-month ${isToday ? 'today' : ''} ${
            isSelected ? 'selected' : ''
          } ${isSelectable ? 'selectable' : 'not-selectable'}`}
          onClick={() => isSelectable && handleDateSelect(date)}
        >
          <span className="day-number">{day}</span>
          {hasExistingRequest && (
            <div className="status-dot existing-request"></div>
          )}
          {isSelected && <div className="status-dot selected"></div>}
        </div>
      );
    }

    // Add empty cells for remaining days
    const remainingCells = totalCells - (firstDay + daysInMonth);
    for (let i = 1; i <= remainingCells; i++) {
      const nextMonthDate = new Date(currentYear, currentMonth + 1, i);
      days.push(
        <div key={`next-${i}`} className="calendar-day other-month">
          <span className="day-number">{nextMonthDate.getDate()}</span>
        </div>
      );
    }

    return (
      <div className="calendar-container">
        {/* Month Navigation Header */}
        <div className="calendar-header">
          <button
            type="button"
            onClick={() => navigateMonth('prev')}
            className="nav-button"
            aria-label="Previous month"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <div className="header-center">
            <h4 className="month-year-title">
              {monthNames[currentMonth]} {currentYear}
            </h4>
            <button type="button" onClick={goToToday} className="today-button">
              Go to Today
            </button>
          </div>

          <button
            type="button"
            onClick={() => navigateMonth('next')}
            className="nav-button"
            aria-label="Next month"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        {/* Day Headers */}
        <div className="calendar-day-headers">
          {dayNames.map(day => (
            <div key={day} className="day-header">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="calendar-grid">{days}</div>

        {/* Legend */}
        <div className="calendar-legend">
          <div className="legend-item">
            <div className="status-dot selected"></div>
            <span>Selected</span>
          </div>
          <div className="legend-item">
            <div className="status-dot existing-request"></div>
            <span>Existing Request</span>
          </div>
          <div className="legend-item">
            <div className="status-dot today"></div>
            <span>Today</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="apply-leave-container">
      <div className="mb-8 text-center">
        <h3 className={typography.h3} style={{ color: '#000000' }}>
          Apply for Leave
        </h3>
        <p
          className={typography.p}
          style={{ color: '#666666', marginTop: '0.5rem' }}
        >
          Select dates, choose leave type, and provide a reason for your leave
          request
        </p>
      </div>

      {success && (
        <div className="success-message mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          ✅ Leave request submitted successfully!
        </div>
      )}

      {error && (
        <div className="error-message mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          ❌ {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Leave Balance - Removed as not available in backend yet */}
        {/* 
        {leaveBalance && (
          <Card.Gradient className="p-3">
            <h5
              className={typography.h5}
              style={{ color: '#000000', marginBottom: '1rem' }}
            >
              Your Leave Balance
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {Object.entries(leaveBalance).map(([type, balance]) => (
                <div
                  key={type}
                  className="text-center p-3 bg-white rounded-lg shadow-sm"
                >
                  <div className="text-2xl font-bold text-blue-600">
                    {balance}
                  </div>
                  <div className="text-sm text-gray-600 capitalize">
                    {type.replace('_', ' ')}
                  </div>
                </div>
              ))}
            </div>
          </Card.Gradient>
        )}
        */}

        {/* Date Selection */}
        <Card.Gradient className="p-3 overflow-x-hidden w-full">
          <h5
            className={typography.h5}
            style={{ color: '#000000', marginBottom: '1rem' }}
          >
            Select Dates
          </h5>
          <p
            className={typography.p}
            style={{ color: '#666666', marginBottom: '1rem' }}
          >
            Click on dates to select them. You can select multiple dates for
            consecutive leave.
          </p>

          <div className="w-full overflow-x-hidden">
            {renderCalendar()}
          </div>

          {selectedDates.length > 0 && (
            <div className="mt-4">
              <h6
                className={typography.h6}
                style={{ color: '#000000', marginBottom: '0.5rem' }}
              >
                Selected Dates:
              </h6>
              <div className="flex flex-wrap gap-2">
                {selectedDates.map((date, index) => (
                  <div key={index} className="selected-date-chip">
                    <span>{leaveApi.formatDateForDisplay(date)}</span>
                    <button
                      type="button"
                      onClick={() => removeDate(date)}
                      className="remove-date-btn"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card.Gradient>

        {/* Leave Type Selection */}
        <Card.Gradient className="p-2">
          <h5
            className={typography.h5}
            style={{ color: '#000000', marginBottom: '1rem' }}
          >
            Leave Type
          </h5>
          <select
            value={selectedLeaveType}
            onChange={e => setSelectedLeaveType(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">Select leave type</option>
            {leaveTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </Card.Gradient>

        {/* Reason */}
        <Card.Gradient className="p-3">
          <h5
            className={typography.h5}
            style={{ color: '#000000', marginBottom: '1rem' }}
          >
            Reason for Leave
          </h5>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Please provide a reason for your leave request..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={4}
            required
          />
        </Card.Gradient>

        {/* Submit Button */}
        <div className="text-center">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-8 py-3 rounded-lg font-medium text-white transition-colors ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
            }`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Leave Request'}
          </button>
        </div>
      </form>

      <style>{`
        .apply-leave-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 0.5rem;
          width: 100%;
          box-sizing: border-box;
          overflow-x: hidden;
        }

        .calendar-container {
          background: white;
          border-radius: 12px;
          padding: 0.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        @media (min-width: 640px) {
          .calendar-container {
            padding: 0.75rem;
          }
          .day-header {
            padding: 0.75rem 0.5rem;
          }
        }

        .calendar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #f3f4f6;
        }

        .header-center {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .nav-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s;
        }

        .nav-button:hover {
          border-color: #3b82f6;
          color: #3b82f6;
          background: #f0f9ff;
        }

        .month-year-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
        }

        .today-button {
          padding: 0.25rem 0.75rem;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .today-button:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        .calendar-day-headers {
          display: grid;
          grid-template-columns: repeat(7, minmax(0, 1fr));
          gap: 2px;
          margin-bottom: 1rem;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .day-header {
          padding: 0.5rem 0.25rem;
          text-align: center;
          font-weight: 600;
          color: #6b7280;
          font-size: 0.875rem;
          background: #f9fafb;
          border-radius: 6px;
          min-width: 0;
          overflow: hidden;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, minmax(0, 1fr));
          gap: 2px;
          margin-bottom: 1.5rem;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .calendar-day {
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          cursor: pointer;
          position: relative;
          transition: all 0.2s;
          min-height: 40px;
          min-width: 0;
          max-width: 100%;
          box-sizing: border-box;
          overflow: hidden;
        }

        .calendar-day.current-month {
          background: #f8fafc;
          color: #374151;
        }

        .calendar-day.other-month {
          background: #f9fafb;
          color: #9ca3af;
        }

        .calendar-day.today {
          border: 2px solid #3b82f6;
          font-weight: bold;
          background: #dbeafe;
          color: #1e40af;
        }

        .calendar-day.selectable:hover {
          background: #e0f2fe;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .calendar-day.selected {
          background: #3b82f6;
          color: white;
          font-weight: 600;
        }

        .calendar-day.not-selectable {
          background: #fef2f2;
          color: #ef4444;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .day-number {
          font-size: 14px;
          font-weight: 500;
          z-index: 1;
        }

        .status-dot {
          position: absolute;
          bottom: 4px;
          right: 4px;
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        .status-dot.selected {
          background: white;
          box-shadow: 0 0 0 1px #3b82f6;
        }

        .status-dot.existing-request {
          background: #10b981;
        }

        .status-dot.today {
          background: #3b82f6;
        }

        .calendar-legend {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          font-size: 12px;
          color: #6b7280;
          flex-wrap: wrap;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.25rem 0.5rem;
          background: #f9fafb;
          border-radius: 6px;
        }

        .selected-date-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #3b82f6;
          color: white;
          padding: 0.5rem 0.75rem;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
        }

        .remove-date-btn {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          font-size: 16px;
          line-height: 1;
          padding: 0;
          margin-left: 0.25rem;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .remove-date-btn:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>
    </div>
  );
};

export default ApplyLeave;
